import { AlarmOff, MoreVert } from '@mui/icons-material';
import { Grid2, styled, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import { Md } from '@next-box/i18n/client';
import { PosterImage } from '#components/PosterImage/index.tsx';
import { TileSkeleton } from '#components/Tile/Skeleton.tsx';
import { Tile } from '#components/Tile/index.tsx';
import { type WatchlistCategory } from '#components/Watchlist/WatchlistCategory.ts';
import { type WatchlistTileItem } from '#components/Watchlist/helpers/formatWatchlist.ts';
import { mapScreenTypeToSectionType } from '#helpers/enumMappers.ts';
import { tt } from '#i18n/index.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';

const FallbackContainer = styled('div')(() => ({
  maxWidth: '600px',
}));

export type WatchlistTabPanelProps = {
  activeCategory: WatchlistCategory;
  categoryItems: WatchlistTileItem[];
  loading: boolean;
  setActivePreview?: (item: ActivePreview) => void;
};

export const WatchlistTabPanel = ({ categoryItems, loading, setActivePreview }: WatchlistTabPanelProps) => {
  const theme = useTheme();
  const hasWatchlist = useStore(store => store.hasWatchlist);
  const fallbackContent = tt('watchlist.UPCOMING.fallbackContent');

  return (
    <Grid2 container spacing={0}>
      {categoryItems.length > 0 ? (
        categoryItems.map(({ id, type, ...rest }, index) => {
          return (
            <Grid2
              key={`${String(id)}-${String(index)}`}
              size={{ lg: 6, sm: 12 }}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  paddingLeft: 0,
                  paddingRight: theme.spacing(1),
                },
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                },
                padding: theme.spacing(1.5),
              }}
            >
              <Tile
                {...rest}
                id={id}
                orientation="horizontal"
                renderImage={imageProps => (
                  <PosterImage
                    {...imageProps}
                    showMoreOptions={false}
                    sx={{
                      flexShrink: 0,
                      width: '100px',
                      [theme.breakpoints.up('md')]: { width: '125px' },
                    }}
                  />
                )}
                setActivePreview={setActivePreview}
                showMoreOptions
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    '.tagline': {
                      display: 'none',
                    },
                  },
                }}
                type={mapScreenTypeToSectionType(type)}
              />
            </Grid2>
          );
        })
      ) : loading ? (
        Array.from({ length: 4 }).map((_, index) => {
          return (
            <Grid2
              key={index}
              size={{ lg: 6, sm: 12 }}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  paddingLeft: 0,
                  paddingRight: theme.spacing(1),
                },
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                },
                padding: theme.spacing(1.5),
              }}
            >
              <TileSkeleton orientation="horizontal" />
            </Grid2>
          );
        })
      ) : hasWatchlist() ? undefined : (
        <FallbackContainer>
          <Md
            componentMapper={{
              AlarmOff: { component: AlarmOff, props: { sx: { color: green[500], verticalAlign: 'text-bottom' } } },
              MoreVert: { component: MoreVert, props: { sx: { verticalAlign: 'text-bottom' } } },
            }}
          >
            {fallbackContent}
          </Md>
        </FallbackContainer>
      )}
    </Grid2>
  );
};
